import type { WorkflowStageDefinition } from "@/types"
import { OIO_STAGES } from "@/features/oio/config/oio.config"

/**
 * Order-type (service) registry: the ONE place that maps a case's
 * `metadata.service_id` onto the agent names, labels and ownership rules the
 * shared shell needs.
 *
 * Why it exists: the shell (CopilotKitProvider, GenerationTracker,
 * DraftingRoute, case lists) used to hard-code "oio". A second order type on
 * the same shell must be routed by DATA ON THE CASE, never by a flag, and every
 * shell piece has to agree on which agent a case belongs to. If two pieces
 * disagree (e.g. CopilotKit runs "oia" while the poller asks "oio"), the OIO
 * run-status route answers `draft_not_started` for the appeal and the poller
 * writes that back into the thread, resetting a running appeal to its intake
 * page (frontend map, gotcha G1).
 *
 * LIGHT BY CONTRACT: Nav, Home, Vault (through case-view), CopilotKitProvider,
 * GenerationTracker and DraftingRoute all import this module. It must only
 * import types and plain data. Importing a workspace, hook or component here
 * would pull that bundle into every screen, and OIO users would download the
 * OIA workspace, which DraftingRoute lazy-loads for exactly that reason.
 * services.registry.test.ts fails if a non-data import appears.
 */

/** Canonical order-type ids. `metadata.service_id` resolves to one of these. */
export type ServiceId = "oio" | "oia"

/** The service a thread belongs to when its metadata carries no (or an unknown) id. */
export const DEFAULT_SERVICE_ID: ServiceId = "oio"

export interface ServiceDescriptor {
  /** Canonical id, as written to `metadata.service_id` by createThread. */
  serviceId: ServiceId
  /**
   * CopilotKit agent key: the `agent` prop on <CopilotKit> and the
   * `useCoAgent({ name })` a workspace reads. Must equal a key of the agents
   * map in copilotkit-runtime/server.mjs, or CopilotKit throws
   * "Agent '<name>' not found" at mount.
   */
  agentName: string
  /**
   * Agent that owns post-draft edit runs. OIO has a separate rewrite graph
   * (`oio_rewrite` on /rewrite/stream). OIA revise runs go through the SAME
   * `oia` agent on /oia/stream (design D3), so it is `oia` here.
   */
  rewriteAgentName: string
  /**
   * The `{agent_name}` path segment of
   * /api/v1/storage/runs/{agent_name}/{thread_id}/(new-attempt|status).
   * The run coordinator keys durable runs by this name, so a new-attempt
   * marker or a status poll under any other name talks about a different run.
   */
  runsAgentName: string
  /** Officer-facing order type, e.g. "Order-in-Original". */
  title: string
  /** Short badge used in case refs and copy, e.g. "OIO". */
  badge: string
  /** `Icon` name for case lists. */
  icon: string
  /**
   * Pipeline stages the shell's progress pieces iterate. `null` means the
   * workspace owns its stage list and the shell never renders this order
   * type's progress (OIA: `features/oia/config`, mirrored from the backend's
   * `oia/stages.py`). Pass it to `useLiveThoughts` / `deriveActiveStage`.
   */
  stages: readonly WorkflowStageDefinition[] | null
  /**
   * Who owns the case's run status.
   *  - "shell": the always-mounted GenerationTracker mirrors live progress into
   *    useGenerationStore and its reconnect poller adopts the end state from
   *    GET /api/v1/storage/runs/{runsAgentName}/{id}/status.
   *  - "workspace": the shell does neither. The workspace reads status from its
   *    own source and, if it wants the nav/Vault "running" dot, writes
   *    useGenerationStore itself. OIA is here because the shared run-status
   *    route cannot tell a finished draft from a finished revise (both are
   *    `done`) and speaks the OIO status vocabulary; OIA status comes from
   *    GET /api/v1/oia/cases/{id} (design D3).
   */
  statusOwner: "shell" | "workspace"
  /**
   * `metadata.draft_status` values that end a run. CopilotKitProvider adopts a
   * `done` value silently on a stream error (the run finished, the transport
   * merely dropped) and surfaces an error only for a `failed` value, because
   * the backend is the sole authority on failure.
   */
  terminalStatuses: { done: readonly string[]; failed: readonly string[] }
  /**
   * Prefix of this order type's per-case localStorage keys. Convention:
   * `<prefix>-<name>-<threadId>` (or `<prefix>-<name>:<threadId>`), built with
   * `caseStorageKey`. purgeCaseClientState scans for it on case delete, so a key
   * that follows the convention is cleaned up without editing the purge list.
   */
  storageKeyPrefix: string
}

export const SERVICE_REGISTRY: Readonly<Record<ServiceId, ServiceDescriptor>> = {
  oio: {
    serviceId: "oio",
    agentName: "oio",
    rewriteAgentName: "oio_rewrite",
    runsAgentName: "oio",
    title: "Order-in-Original",
    badge: "OIO",
    icon: "gavel",
    stages: OIO_STAGES,
    statusOwner: "shell",
    terminalStatuses: {
      done: ["draft_completed", "draft_filed"],
      failed: ["draft_failed_terminal"],
    },
    storageKeyPrefix: "oio",
  },
  oia: {
    serviceId: "oia",
    agentName: "oia",
    rewriteAgentName: "oia",
    runsAgentName: "oia",
    title: "Order-in-Appeal",
    badge: "OIA",
    icon: "scale",
    stages: null,
    statusOwner: "workspace",
    terminalStatuses: {
      // design 5.4: draft_ready (cards are advice, not gates), draft_flagged
      // (delivered with a fatal gate unresolved — readable and revisable, not
      // signable), draft_blocked (pre-flagged-delivery cases) and
      // intake_blocked (a document-integrity check failed) are all delivered
      // outcomes, not transport failures. draft_filed is the shared "filed"
      // marker the officer toggles.
      done: ["draft_ready", "draft_flagged", "draft_blocked", "intake_blocked", "draft_filed"],
      failed: ["failed"],
    },
    storageKeyPrefix: "oia",
  },
}

/**
 * Legacy and stray ids that must resolve to a canonical service.
 *
 * - "apl": the Appeals picker card and case-view used it as a placeholder id
 *   before OIA existed. No thread was created with it (the card was disabled),
 *   but anything carrying it is an appeal and must reach the OIA machinery,
 *   never OIO.
 * - "oio_rewrite": ThreadProvider.activeServiceId is vestigial; OIOWorkspace
 *   writes "oio_rewrite" into it and createThread falls back to it when no
 *   service id is passed, so a thread can carry it. It is an OIO case.
 */
const SERVICE_ID_ALIASES: Readonly<Record<string, string>> = {
  apl: "oia",
  oio_rewrite: "oio",
}

/**
 * Normalise a raw `metadata.service_id`: trimmed, lower-cased, aliases mapped,
 * and absent/empty read as the default ("oio", which every thread created
 * before the registry carries or omits). Unknown ids (e.g. "scn", "rfd") are
 * returned as-is so case-view can still label them; `getService` maps them to
 * the default descriptor.
 */
export function canonicalServiceId(raw: unknown): string {
  const id = typeof raw === "string" ? raw.trim().toLowerCase() : ""
  if (!id) return DEFAULT_SERVICE_ID
  return SERVICE_ID_ALIASES[id] ?? id
}

export function isServiceId(id: string): id is ServiceId {
  return Object.prototype.hasOwnProperty.call(SERVICE_REGISTRY, id)
}

/**
 * The descriptor for a raw service id. Unknown ids fall back to OIO: before the
 * registry every thread, whatever it carried, was driven by the OIO machinery,
 * and no other order type is built yet.
 */
export function getService(raw: unknown): ServiceDescriptor {
  const id = canonicalServiceId(raw)
  return isServiceId(id) ? SERVICE_REGISTRY[id] : SERVICE_REGISTRY[DEFAULT_SERVICE_ID]
}

/** The descriptor for a thread (null-safe: no thread, no metadata -> OIO). */
export function serviceForThread(
  thread: { metadata?: { service_id?: unknown } | null } | null | undefined,
): ServiceDescriptor {
  return getService(thread?.metadata?.service_id)
}

/** Build a per-case localStorage key that purgeCaseClientState will clean up. */
export function caseStorageKey(
  service: ServiceDescriptor,
  name: string,
  threadId: string,
): string {
  return `${service.storageKeyPrefix}-${name}-${threadId}`
}
